// Free "Unfiltered" pack page: send the form without leaving the page, swap in
// the thank-you state, and copy the coupon.
//
// The form works without any of this — it is a real <form> posting to the REST
// endpoint, so a visitor with broken JS still gets the pack by email. What this
// adds is the instant hand-off: the download link comes back in the response
// and appears as a button, so nobody waits on an inbox to get what they asked
// for.

import { getLenis, prefersReducedMotion, scrollToEl } from '../lib/lenis';

interface SubmitResponse {
  ok?: boolean;
  download?: string;
  coupon?: string;
  message?: string;
}

/** Fire the conversion into whatever analytics the page happens to carry. */
function trackLead(pack: string): void {
  const w = window as unknown as {
    dataLayer?: unknown[];
    fbq?: (...args: unknown[]) => void;
  };
  w.dataLayer?.push({ event: 'unfiltered_lead', pack });
  w.fbq?.('track', 'Lead', { content_name: pack });
}

function showSuccess(root: HTMLElement, data: SubmitResponse): void {
  const success = root.querySelector<HTMLElement>('[data-unf-success]');
  if (!success) return;

  const link = success.querySelector<HTMLAnchorElement>('[data-unf-download]');
  if (link && data.download) link.href = data.download;

  root.classList.add('is-done');
  success.hidden = false;

  // The flow above is now display:none, so the page is shorter than the scroll
  // position it was left at.
  const lenis = getLenis();
  if (lenis) lenis.scrollTo(0, { immediate: true });
  else window.scrollTo(0, 0);
}

function bindForm(root: HTMLElement, form: HTMLFormElement): void {
  const msg = form.querySelector<HTMLElement>('[data-unf-msg]');
  const openedAt = Date.now();

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (form.classList.contains('is-sending')) return;

    const data = new FormData(form);
    const body = {
      pack: root.dataset.pack ?? '',
      name: String(data.get('name') ?? ''),
      email: String(data.get('email') ?? ''),
      phone: String(data.get('phone') ?? ''),
      company: String(data.get('company') ?? ''),
      started: openedAt,
    };

    form.classList.remove('is-error');
    form.classList.add('is-sending');
    if (msg) msg.textContent = form.dataset.sending ?? '';

    try {
      const res = await fetch(form.action, {
        method: 'POST',
        // No X-WP-Nonce: the page is served from the host's page cache, so a
        // nonce baked into it goes stale within a day and WordPress then
        // rejects every anonymous submission ("cookie check failed"). The
        // endpoint is public and carries its own guards (inc/unfiltered.php).
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const json = (await res.json().catch(() => null)) as SubmitResponse | null;

      if (!res.ok || !json?.ok) {
        throw new Error(json?.message || form.dataset.error || '');
      }

      trackLead(document.title);
      showSuccess(root, json);
    } catch (err) {
      form.classList.add('is-error');
      if (msg) {
        msg.textContent =
          err instanceof Error && err.message ? err.message : (form.dataset.error ?? '');
      }
    } finally {
      form.classList.remove('is-sending');
    }
  });
}

/** Copy the coupon, and say so on the button itself. */
function bindCopy(root: HTMLElement): void {
  root.querySelectorAll<HTMLButtonElement>('[data-unf-copy]').forEach((btn) => {
    const hint = btn.querySelector<HTMLElement>('[data-unf-copy-hint]');
    const idle = hint?.textContent ?? '';

    btn.addEventListener('click', async () => {
      const code = btn.dataset.unfCopy ?? '';
      try {
        await navigator.clipboard.writeText(code);
      } catch {
        return; // No clipboard permission — the code is on screen anyway.
      }
      if (!hint) return;
      hint.textContent = hint.dataset.copied ?? idle;
      window.setTimeout(() => {
        hint.textContent = idle;
      }, 2000);
    });
  });
}

/** In-page links to the form, through Lenis like every other anchor. */
function bindScrollers(root: HTMLElement): void {
  const form = root.querySelector<HTMLElement>('[data-unf-form]');
  if (!form) return;

  root.querySelectorAll<HTMLAnchorElement>('[data-unf-scroll]').forEach((link) => {
    link.addEventListener('click', (event) => {
      event.preventDefault();
      scrollToEl(form, -90);
      form.querySelector<HTMLInputElement>('input[name="name"]')?.focus({ preventScroll: true });
    });
  });
}

/** The pack leans toward the pointer — a few degrees, mouse only. */
function bindTilt(root: HTMLElement): void {
  const pack = root.querySelector<HTMLElement>('[data-unf-tilt]');
  if (!pack || prefersReducedMotion()) return;
  if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

  const area = pack.parentElement ?? pack;

  area.addEventListener(
    'pointermove',
    (event) => {
      const rect = area.getBoundingClientRect();
      const x = (event.clientX - rect.left) / rect.width - 0.5;
      const y = (event.clientY - rect.top) / rect.height - 0.5;
      pack.style.transform = `rotate(${-2.2 + x * 2.4}deg) translate3d(${x * 14}px, ${y * 10}px, 0)`;
    },
    { passive: true }
  );

  area.addEventListener('pointerleave', () => {
    pack.style.transform = '';
  });
}

export function initUnfiltered(): void {
  const root = document.querySelector<HTMLElement>('[data-unfiltered]');
  if (!root) return;

  const form = root.querySelector<HTMLFormElement>('[data-unf-form]');
  if (form) bindForm(root, form);

  bindCopy(root);
  bindScrollers(root);
  bindTilt(root);
}
